import { useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { Button } from "../components/ui/button.js";
import { Card, CardContent } from "../components/ui/card.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";

/** Passwordless sign-in: request a magic link. In dev the link is returned. */
export function LoginPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.requestMagicLink(email);
      setSent(true);
      setDevLink(res.devMagicLink ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid h-full place-items-center p-6">
      <Card className="w-full max-w-sm">
        <CardContent className="p-8">
          <h1 className="text-xl font-semibold">Sign in to Epicredible</h1>
          <p className="mb-6 mt-1 text-sm text-muted-foreground">
            We&apos;ll email you a secure sign-in link — no password needed.
          </p>
          {sent ? (
            <div className="text-sm">
              <p>Check your inbox for a sign-in link.</p>
              {devLink && (
                <p className="mt-3 break-all">
                  <span className="text-muted-foreground">Dev link:</span>{" "}
                  <a className="font-medium text-[var(--color-link)]" href={devLink}>
                    {devLink}
                  </a>
                </p>
              )}
            </div>
          ) : (
            <form onSubmit={submit}>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@institution.edu"
              />
              {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
              <Button type="submit" className="mt-4 w-full" disabled={busy}>
                {busy ? "Sending…" : "Send sign-in link"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
